<template>
  <v-container>
    <v-card class="mx-auto pa-4">
      <p class="display-1 text--primary">Seller Ratings</p>
      <v-row dense>
        <v-col cols="12" md="5">
          <v-text-field v-model="searchText" label="Search by user ID or store name" prepend-inner-icon="mdi-magnify" clearable hide-details />
        </v-col>
        <v-col cols="6" md="3">
          <v-select v-model="tier" :items="tierFilterItems" label="Tier" clearable hide-details />
        </v-col>
        <v-col cols="6" md="4">
          <v-switch v-model="ratedOnly" label="Rated sellers only (turn off to search all sellers)" hide-details class="mt-2" />
        </v-col>
      </v-row>
      <v-data-table :headers="headers" :items="sellers" :loading="loading" loading-text="Loading...please wait"
        class="elevation-1 mt-4" :options.sync="options" :server-items-length="totalItems" :footer-props="{
          itemsPerPageOptions: [10, 20, 100, 500, -1]
        }">
        <template v-slot:item.userID="{ item }">
          <a v-if="item.eBayStoreURL" :href="item.eBayStoreURL" target="_blank">{{ decodeURIComponent(item.userID) }}</a>
          <span v-else>{{ decodeURIComponent(item.userID) }}</span>
        </template>
        <template v-slot:item.marketplaceName="{ item }">
          {{ marketplaceName(item) }}
        </template>
        <template v-slot:item.tier="{ item }">
          <v-chip small label :color="tierColor(item.tier)" dark>{{ item.tier }}</v-chip>
        </template>
        <template v-slot:item.weight="{ item }">
          &times;{{ item.weight }}
        </template>
        <template v-slot:item.feedback="{ item }">
          <span v-if="item.feedbackScore != null">{{ item.feedbackScore }}</span>
          <span v-if="item.positiveFeedbackPercent != null"> ({{ item.positiveFeedbackPercent }}%)</span>
        </template>
        <template v-slot:item.notes="{ item }">
          <span class="notes">{{ item.notes }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn small color="primary" class="mr-2" @click="editSeller(item)">
            <v-icon left small>mdi-pencil</v-icon>Edit
          </v-btn>
          <v-btn v-if="item.tier === 'Excluded'" color="success" small @click="includeSeller(item)" :loading="savingId === item.id">
            Include
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <SellerSettingsDialog ref="sellerSettings" />
  </v-container>
</template>

<script>
import { MarketplacesService } from '../services/api/marketplaces';
import { SellersService, SELLER_TIERS, SELLER_TIER_COLORS } from '../services/api/sellers';
import SellerSettingsDialog from '../components/sellers/seller-settings-dialog.vue';

const apiSellers = new SellersService();

export default {
  name: 'Sellers',
  components: {
    SellerSettingsDialog,
  },
  data() {
    return {
      sellers: [],
      loading: false,
      savingId: null,
      totalItems: 0,
      marketplaces: [],
      searchText: '',
      searchTimeout: null,
      tier: this.$route.query.tier || null,
      ratedOnly: true,
      tierFilterItems: SELLER_TIERS,
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: ['userID'],
        sortDesc: [false],
      },
      headers: [
        { text: 'User ID', value: 'userID' },
        { text: 'Store Name', value: 'eBayStoreName', sortable: false },
        { text: 'Marketplace', value: 'marketplaceName', sortable: false },
        { text: 'Feedback', value: 'feedback', sortable: false },
        { text: 'Tier', value: 'tier' },
        { text: 'Weight', value: 'weight', align: 'end' },
        { text: 'Notes', value: 'notes', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },
  watch: {
    options: {
      handler() {
        this.fetchSellers();
      },
      deep: true,
    },
    searchText() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.resetAndFetch(), 500);
    },
    tier() {
      this.resetAndFetch();
    },
    ratedOnly() {
      this.resetAndFetch();
    },
  },
  methods: {
    tierColor(tier) {
      return SELLER_TIER_COLORS[tier] || 'grey';
    },
    marketplaceName(seller) {
      const marketplace = this.marketplaces.find(m => m.id === seller.marketplaceID);
      return marketplace ? marketplace.name : 'Unknown Marketplace';
    },
    async fetchMarketplaces() {
      const service = new MarketplacesService();
      try {
        const marketplaces = await service.getMarketplaces();
        this.marketplaces = Array.isArray(marketplaces) ? marketplaces : [];
      } catch (error) {
        console.error('Error fetching marketplaces:', error);
        this.marketplaces = [];
      }
    },
    resetAndFetch() {
      if (this.options.page !== 1) {
        // The options watcher will fetch.
        this.options = { ...this.options, page: 1 };
      } else {
        this.fetchSellers();
      }
    },
    async fetchSellers() {
      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;
        const search = {
          searchText: this.searchText || undefined,
          sortOrderField: sortBy.length ? sortBy[0] : 'userID',
          sortDirection: sortDesc.length && sortDesc[0] ? 'desc' : 'asc',
          itemsPerPage,
          itemsToSkip: itemsPerPage > 0 ? (page - 1) * itemsPerPage : 0,
        };
        const result = await apiSellers.getSellers(search, { tier: this.tier, ratedOnly: this.ratedOnly });
        const items = (result && Array.isArray(result.items)) ? result.items : [];
        this.totalItems = result ? result.totalItemCount : 0;
        this.sellers = items;
      } finally {
        this.loading = false;
      }
    },
    async editSeller(item) {
      const updated = await this.$refs.sellerSettings.open(item.id);
      if (updated) {
        this.fetchSellers();
      }
    },
    async includeSeller(item) {
      this.savingId = item.id;
      try {
        await apiSellers.includeSeller(item.id);
        this.fetchSellers();
      } finally {
        this.savingId = null;
      }
    },
  },
  mounted() {
    this.fetchMarketplaces();
    this.fetchSellers();
  },
};
</script>

<style scoped>
.notes {
  white-space: pre-line;
}
</style>
